// Creating an invitation link.
//
// One component for all three kinds, because from the sender's point of view
// they are the same action — make a link, send it — and only the sentence
// describing what the recipient gets differs:
//
//   app   — "come and train with me", the general invitation. No target.
//   group — the above, plus membership of one of your groups.
//   live  — the above, plus a seat in the session you are hosting right now.
//
// Accepting any of them makes the two people mates; that is the part every
// invite has in common, and it is stated on the dialog rather than left as a
// surprise.

import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AlertCircle, Check, Copy, Loader2, UserPlus } from "lucide-react";
import { useToast } from "../../contexts/ToastContext";
import { createInvite } from "../../services/api";
import { buildInviteUrl, shareOrCopyLink } from "../../utils/shareLinks";

const EXPIRY_OPTIONS = [
  { value: "never", label: "Never expires" },
  { value: "1", label: "Expires in 1 day" },
  { value: "7", label: "Expires in 7 days" },
  { value: "30", label: "Expires in 30 days" },
];

const COPY = {
  app: {
    title: "Invite a mate",
    description:
      "Send this link to anyone. They can sign in with Google and you'll be connected.",
    gets: "They'll join Khronos and you'll be added as mates.",
  },
  group: {
    title: "Invite to this group",
    description:
      "Anyone with this link can join the group — send it only to people you want in it.",
    gets: "They'll join the group and be added to your mates.",
  },
  live: {
    title: "Invite to this live session",
    description:
      "Drops whoever opens it straight into the session you're hosting.",
    gets: "They'll join the live session and be added to your mates.",
  },
};

const InviteDialog = ({
  open,
  onOpenChange,
  kind = "app",
  groupId,
  liveSessionId,
}) => {
  const { showToast } = useToast();

  const [note, setNote] = useState("");
  const [singleUse, setSingleUse] = useState(false);
  const [expiry, setExpiry] = useState("never");
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const copy = COPY[kind] || COPY.app;

  useEffect(() => {
    if (!open) return;
    setNote("");
    setSingleUse(false);
    setExpiry("never");
    setInvite(null);
    setError(null);
    setCopied(false);
  }, [open]);

  const inviteUrl = invite ? buildInviteUrl(invite.code) : "";

  const handleCreate = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      const created = await createInvite({
        kind,
        group_id: kind === "group" ? groupId : undefined,
        live_session_id: kind === "live" ? liveSessionId : undefined,
        note: note.trim() || null,
        max_uses: singleUse ? 1 : null,
        expires_in_days: expiry === "never" ? null : Number(expiry),
      });
      setInvite(created);

      const outcome = await shareOrCopyLink({
        url: buildInviteUrl(created.code),
        title: "Train with me on Khronos",
        text: note.trim() || "Join me on Khronos",
      });
      if (outcome === "copied") setCopied(true);
    } catch (err) {
      setError(err.message || "Could not create the invitation.");
    } finally {
      setCreating(false);
    }
  }, [kind, groupId, liveSessionId, note, singleUse, expiry]);

  const handleCopy = useCallback(async () => {
    const outcome = await shareOrCopyLink({
      url: inviteUrl,
      title: "Train with me on Khronos",
      text: note.trim() || "Join me on Khronos",
    });
    if (outcome === "copied") {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast({ title: "Invitation link copied" });
    } else if (outcome === "failed") {
      setError("Could not copy the link. Select and copy it manually.");
    }
  }, [inviteUrl, note, showToast]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            {copy.title}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        {invite ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                readOnly
                value={inviteUrl}
                onFocus={(e) => e.target.select()}
                className="min-w-0 font-mono text-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                title="Copy link"
                aria-label="Copy invitation link"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {copy.gets}
              {singleUse
                ? " This link works once."
                : " Anyone you send it to can use it."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-note">Message (optional)</Label>
              <Input
                id="invite-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Come train with me"
                maxLength={280}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="pr-3">
                <Label htmlFor="invite-single-use">Single use</Label>
                <p className="text-xs text-muted-foreground">
                  The link stops working after one person accepts.
                </p>
              </div>
              <Switch
                id="invite-single-use"
                checked={singleUse}
                onCheckedChange={setSingleUse}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Expiry</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {invite ? "Done" : "Cancel"}
          </Button>
          {!invite && (
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Create link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteDialog;
